import i18n from '../../../i18n'
import { FormInputConfig, FormInputItem } from '../../../utils/formUtils/types'

interface FormikError {
    key: string
    value: string
}
interface FormikErrors {
    [key: string]: string | string[] | FormikErrors | FormikErrors[] | undefined
}
export interface ModalError {
    key: string
    label: string
}

const LANGUAGE_CODES = ['hr', 'en']

/**
 * Recursively flattens a nested formik errors object into an array of {key, value} pairs.
 *
 * Handles four nesting depths:
 * - Flat:   { price: 'error' }                                 → [{ key: 'price', value: 'error' }]
 * - Nested: { general: { price: 'error' } }                    → [{ key: 'price', value: 'error' }]
 * - Deep:   { description: { hr: { title: 'error' } }          → [{ key: 'title', value: 'error' }]
 * - Array:  { intermediaryDiaryBuyer: { 0: { name: 'error' } } → [{ key: 'intermediaryDiaryBuyer', value: 'error' }]
 */
export const transformValidationErrors = (obj: FormikErrors, path?: string): FormikError[] => {
    return Object.entries(obj).reduce<FormikError[]>((acc, [key, value]) => {
        const currentPath = path ? `${path}.${key}` : key

        // Final error message
        if (typeof value === 'string') {
            const segments = currentPath.split('.')

            // Check if path contains array index (numeric segment)
            const arrayIndexPos = segments.findIndex(seg => /^\d+$/.test(seg))

            let fieldName: string
            if (arrayIndexPos !== -1) {
                // For array fields (e.g., intermediaryDiaryBuyer.0.name),
                // use the array field name (segment before the index)
                fieldName = segments[arrayIndexPos - 1] ?? segments[0]
            } else {
                // Original logic for non-array fields
                fieldName = segments.reverse().find((segment) => !LANGUAGE_CODES.includes(segment)) ?? segments[0]
            }

            acc.push({ key: fieldName, value })
        }
        // If value is nested FormikErrors object or array, recurse
        else if (typeof value === 'object' && value !== null) {
            const nestedErrorsArr = transformValidationErrors(value as FormikErrors, currentPath)

            acc.push(...nestedErrorsArr)
        }

        return acc
    }, [])
}

/**
 * Recursively extracts {name, label} pairs from nested input configurations into a flat Map for quick lookup.
 * Example: {"property_service_id" => "Usluga"}
 */
const buildFieldLabelMap = (inputs: FormInputConfig[]): Map<string, string> => {
    const map = new Map<string, string>()

    const extract = (item: FormInputConfig | FormInputConfig[] | FormInputItem | FormInputItem[] | FormInputItem[][]) => {
        if (!item) return

        // Iterates each FormInputConfig[] type
        if (Array.isArray(item)) {
            item.forEach(extract)
            return
        }

        // Object with sideForm/mainForm structure (FormInputConfig type)
        if ('sideForm' in item || 'mainForm' in item) {
            const config = item as FormInputConfig
            if (config.sideForm) extract(config.sideForm)
            if (config.mainForm) extract(config.mainForm)
            return
        }

        // Grouped item with children
        if ('children' in item && item.children?.length) {
            item.children.forEach(extract)
            return
        }

        // Sets name from item (Field type)
        if ('name' in item && (('required' in item && item.required) || 'isRequired' in item)) {
            map.set(item.name, item.label || '')
        }
    }

    extract(inputs)

    return map
}

/**
 * Resolves formik errors for modal display.
 * Only errors with a matching label in inputs are included — fields without a label are skipped.
 */
export const resolveValidationErrorLabels = (errors: FormikErrors, inputs: FormInputConfig[]): ModalError[] => {
    const errorEntries = transformValidationErrors(errors)
    if (!errorEntries.length) return []

    const labelMap = buildFieldLabelMap(inputs)

    return errorEntries.reduce<ModalError[]>((acc, { key }) => {
        // case for documents edit form which has global error
        if (key === 'general') {
            acc.push({ key, label: i18n.t('form.errors.documents.general') })
        } else {
            const label = labelMap.get(key)
            if (label) acc.push({ key, label })
        }

        return acc
    }, [])
}
